import { useTranslations } from '@wui/common-library'
import { reactive, readonly, ref, Ref, watch } from 'vue'
import { createI18n, I18n, VueI18n } from 'vue-i18n'

import { pathNames } from '@/shared/utils/constants'
import { getAppURL } from '@/shared/utils/electron'
import { useFetchApi } from '@/shared/utils/fetch'
import { baseUrl, isElectron, tauriMode, isDevelopment } from '@/shared/utils/import-meta'
import { isPWAWindow } from '@/shared/utils/pwa'

import { pluralizationRulesRu } from './pluralization-rules-ru'
import { resourcesByRoute } from './resources-by-route'
import { Locales, Localization, LocalizationResource, Resources } from './types'

/** Last UI locale (survives reload before IM preferences arrive). Same key as PWA_MANIFEST_BOOT_SCRIPT. */
const LOCALE_STORAGE_KEY = 'wc-ui-locale'

const readStoredLocale = (): Locales | null => {
  if (typeof localStorage === 'undefined') return null
  try {
    const raw = localStorage.getItem(LOCALE_STORAGE_KEY)
    if (raw && (Object.values(Locales) as string[]).includes(raw)) {
      return raw as Locales
    }
  } catch {
    /* private mode / quota */
  }
  return null
}

const persistLocale = (locale: Locales) => {
  if (typeof localStorage === 'undefined') return
  try {
    localStorage.setItem(LOCALE_STORAGE_KEY, locale)
  } catch {
    /* ignore */
  }
}

const getManifestLocale = (locale: Locales): string => locale

const isSameManifestHref = (currentHref: string | null, nextHref: string): boolean => {
  if (!currentHref) return false
  if (currentHref === nextHref) return true
  const currentFile = currentHref.split('/').pop()
  const nextFile = nextHref.split('/').pop()
  return Boolean(currentFile && currentFile === nextFile)
}

const updateManifestLink = (locale: Locales) => {
  if (typeof document === 'undefined') return
  if (isDevelopment()) {
    if (isPWAWindow()) document.title = ''
    return
  }
  const manifestLocale = getManifestLocale(locale)
  const base = (import.meta as { env?: { BASE_URL?: string } }).env?.BASE_URL ?? './'
  const href = `${base}manifest-${manifestLocale}.webmanifest`
  const existing = document.querySelector<HTMLLinkElement>('link[rel="manifest"]')
  // Не трогаем href, если boot-скрипт уже выставил тот же locale-manifest:
  // иначе Chrome снова видит смену identity и вешает «Доступно обновление приложения».
  if (isSameManifestHref(existing?.getAttribute('href') ?? null, href)) {
    if (isPWAWindow()) document.title = ''
    return
  }
  if (existing) {
    const link = document.createElement('link')
    link.rel = 'manifest'
    link.href = href
    existing.replaceWith(link)
  } else {
    const link = document.createElement('link')
    link.rel = 'manifest'
    link.href = href
    document.head.appendChild(link)
  }
  if (isPWAWindow()) {
    document.title = ''
  }
}

const currentLocale: Ref<Locales> = ref(readStoredLocale() ?? Locales.RU_RU)
const languagesSelect = reactive(new Map<Locales, Array<string>>())
const messages: {[key in Locales]?: LocalizationResource} = {}
const i18n: I18n = createI18n({
  locale: currentLocale.value,
  fallbackLocale: 'ru-RU',
  messages,
  pluralizationRules: {
    'ru-RU': pluralizationRulesRu,
  },
})

const t = (i18n.global as VueI18n).t

const handleLocaleChange = (v: Locales) => {
  const { setTranslations } = useTranslations()
  if (messages[v]) {
    // Почему-то без этого при первой загрузке ресурсов не получается получить перевод на измененный язык
    setTimeout(() => {
      setTranslations((key: string) => {
        return t(key)
      })
    }, 0)
  }
}

watch(currentLocale, handleLocaleChange)
watch(currentLocale, updateManifestLink, { immediate: true })

export const useLocalization = () => {
  const _fetchResources = (locale: Locales, resources?: string[]): Promise<Localization> => {
    return new Promise((resolve, reject) => {
      // @TODO потом поменять на реальный url
      let url = `${baseUrl()}locales/${locale}.json`

      if (resources) {
        url += '?'
        resources.forEach((resource, i) => {
          if (i > 0) {
            url += '&'
          }

          url += `resource=${resource}`
        })
      }
      // @TODO потом надо будет заменить на запрос
      if (tauriMode()) {
        import('@tauri-apps/api/path').then(({ resolveResource }) => {
          resolveResource(`_up_/dist/locales/${locale}.json`).then((resourcePath) => {
            import('@tauri-apps/plugin-fs').then(({ readTextFile }) => readTextFile(resourcePath).then((result) => {
              resolve(JSON.parse(result))
            }).catch((err) => {
              reject(err)
            }))
          }).catch((err) => {
            reject(err)
          })
        })
      } else if (isElectron()) {
        const useFetch = useFetchApi()
        useFetch.get<Localization>(getAppURL({ pathName: `/locales/${locale}.json`, isLocal: true }))
          .then((data: { data: Localization | PromiseLike<Localization> }) => {
            resolve(data.data)
          })
          .catch((e: Error) => {
            reject(e)
          })
      } else {
        const useFetch = useFetchApi()
        useFetch.get<Localization>(url)
          .then((data: { data: Localization | PromiseLike<Localization> }) => {
            resolve(data.data)
          })
          .catch((e: Error) => {
            reject(e)
          })
      }
    })
  }
  const setLocale = (locale: Locales, routeName?: string): Promise<void> => {
    const oldLocale = currentLocale.value
    return new Promise((resolve, reject) => {
      currentLocale.value = locale
      // Caller should pass current route name; MAIN is a safe fallback (common + chat/calls UI).
      fetchResourcesIfNeed(routeName ?? pathNames.MAIN)
        .then(() => {
          i18n.global.locale = locale
          document.querySelector('html')?.setAttribute('lang', locale)
          persistLocale(locale)

          resolve()
        })
        .catch((e: Error) => {
          console.error('Не удалось загрузить ресурсы локализации при переключении локали')

          currentLocale.value = oldLocale

          reject(e.message)
        })
    })
  }
  const fetchResourcesIfNeed = (pathName: string): Promise<void> => {
    return new Promise((resolve, reject) => {
      const locale = currentLocale.value as Locales
      // Необходимые ресурсы для текущего пути
      const neededResources = resourcesByRoute.get(pathName) ?? []
      // Массив ресурсов, которые нужно загрузить
      let resources: Resources[] = []

      const localeMessages = messages[locale]
      if (localeMessages) {
        neededResources?.forEach((resource: Resources) => {
          if (!localeMessages[resource]) {
            resources.push(resource)
          }
        })

        if (resources.length === 0) {
          resolve()

          return
        }
      } else {
        resources = neededResources
      }

      _fetchResources(locale, resources)
        .then((localization: Localization) => {
          const resources = localization.resources[locale]

          if (!messages[locale]) {
            messages[locale] = {}
          }

          messages[locale] = {
            ...messages[locale],
            ...resources,
          }

          i18n.global.setLocaleMessage(locale, messages[locale] as LocalizationResource)
          if (locale === 'ru-RU') {
            i18n.global.setLocaleMessage('ru', messages[locale] as LocalizationResource)
          }
          handleLocaleChange(currentLocale.value)

          resolve()
        })
        .catch(reject)
    })
  }

  const fetchLanguages = (): Promise<Array<Locales>> => {
    return new Promise((resolve, reject) => {
      const useFetch = useFetchApi()
      useFetch.get<Array<Locales>>(getAppURL({ pathName: '/api/languages' }))
        .then(({ data }: {data: Array<Locales>}) => {
          resolve(data)
        }).catch(reject)
    })
  }

  const _acceptLanguageHandler = (locales: Locales) => {
    switch (locales) {
    case Locales.RU_RU:
      return 'ru-RU,ru;q=0.9,en-GB;q=0.8,en;q=0.7'
    case Locales.EN_GB:
      return 'en-GB,en;q=0.9,ru-RU;q=0.8,ru;q=0.7'
    default:
      return 'ru-RU,ru;q=0.9,en-GB;q=0.8,en;q=0.7'
    }
  }

  type LocalesResources = { [key in Locales]: { [key: string]: unknown } }
  type LanguageResources = { resources: LocalesResources }
  const fetchLanguageResources = (locales: Locales): Promise<LocalesResources> => {
    return new Promise((resolve, reject) => {
      const useFetch = useFetchApi()
      useFetch.get<LanguageResources>(getAppURL({ pathName: `/api/language-resources?lang=${locales}` }), {
        headers: {
          'Accept-Language': _acceptLanguageHandler(locales),
        },
      })
        .then(({ data }: { data: LanguageResources }) => {
          const { resources } = data
          resolve(resources)
        }).catch(reject)
    })
  }

  return {
    currentLocale,
    languagesMap: languagesSelect,
    i18n: readonly(i18n),
    setLocale,
    fetchResourcesIfNeed,
    t,
    tc: (i18n.global as VueI18n).tc,
    fetchLanguages,
    fetchLanguageResources,
  }
}
